/** "Which conversations": everything, chosen organizations, or chosen agents -
 *  one choice for an Insights template (item 70) and a report template (item
 *  71), with the same pickers and the same rule: choosing organizations or
 *  agents means choosing at least one. */
import type { ReactNode } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import type { AgentOption, TenantNode } from '../../types/insights';
import { AgentPicker } from './AgentPicker';
import { OrganizationPicker } from './OrganizationPicker';

export type ScopeKind = 'all' | 'orgs' | 'agents';

export interface Scope { tenant_ids?: string[]; chatbot_ids?: string[] }

export interface ScopeDraft {
  scopeKind: ScopeKind;
  tenantIds: string[];
  /** The agent picker's values (an option may stand for several agent ids, comma-separated). */
  agentValues: string[];
}

/** A stored scope as the choice shows it. */
export function scopeDraftOf(scope: Scope | undefined, agentOptions: AgentOption[]): ScopeDraft {
  const chatbotIds = new Set(scope?.chatbot_ids || []);
  return {
    scopeKind: chatbotIds.size ? 'agents' : (scope?.tenant_ids || []).length ? 'orgs' : 'all',
    tenantIds: scope?.tenant_ids || [],
    agentValues: agentOptions.filter((o) => o.value.split(',').some((id) => chatbotIds.has(id))).map((o) => o.value),
  };
}

/** The choice as the server stores it. */
export function scopeOf(d: ScopeDraft): Scope {
  if (d.scopeKind === 'orgs' && d.tenantIds.length) return { tenant_ids: d.tenantIds };
  if (d.scopeKind === 'agents' && d.agentValues.length) {
    return { chatbot_ids: Array.from(new Set(d.agentValues.flatMap((v) => v.split(',')))) };
  }
  return {};
}

/** The translation key of what is missing ("Choose at least one organization."), or null. */
export function scopeProblem(d: ScopeDraft): string | null {
  if (d.scopeKind === 'orgs' && !d.tenantIds.length) return 'insights.tpl.editor.pickOrgsFirst';
  if (d.scopeKind === 'agents' && !d.agentValues.length) return 'insights.tpl.editor.pickAgentsFirst';
  return null;
}

interface Props {
  value: ScopeDraft;
  onChange: (next: Partial<ScopeDraft>) => void;
  tenantTree: TenantNode[];
  agentOptions: AgentOption[];
  /** What "All" covers, under the choice. */
  allHelp: ReactNode;
  className?: string;
  testIdPrefix: string;
}

export function ScopeChoice({ value, onChange, tenantTree, agentOptions, allHelp, className, testIdPrefix }: Props) {
  const { t } = useLanguage();
  return (
    <>
      <div className={`rp-choices${className ? ` ${className}` : ''}`} role="radiogroup"
           aria-label={t('insights.tpl.editor.whichConversations')}>
        {(['all', 'orgs', 'agents'] as ScopeKind[]).map((kind) => (
          <button key={kind} type="button" role="radio" aria-checked={value.scopeKind === kind} className="rp-choice"
                  onClick={() => onChange({ scopeKind: kind })} data-testid={`${testIdPrefix}-${kind}`}>
            {t(`insights.tpl.editor.scopeKind.${kind}`)}
          </button>
        ))}
      </div>
      {value.scopeKind === 'all' && allHelp}
      {value.scopeKind === 'orgs' && (
        <OrganizationPicker tenantTree={tenantTree} value={value.tenantIds} onChange={(v) => onChange({ tenantIds: v })}
                            label={t('insights.tpl.editor.scopeOrgs')} />
      )}
      {value.scopeKind === 'agents' && (
        <AgentPicker options={agentOptions} value={value.agentValues} onChange={(v) => onChange({ agentValues: v })}
                     label={t('insights.tpl.editor.scopeAgents')} />
      )}
    </>
  );
}
